import { Table } from 'antd';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Column from '../../helpers/columns';
import useDatasource from '../../hooks/useDatasource';
import { FormContext } from '../../hooks/useForm';
import FormWrapper from '../FormWrapper';
import { getRetailPoses } from '../../services/taxonomies/poses';

const RetailPosList = ({ canEdit = true }) => {
  const form = useContext(FormContext);
  const { t } = useTranslation();

  const { value, setValue, errorMessages } = form;
  const { posId, retailerId } = value;

  const columns = useMemo(
    () => [
      Column.text('name', t('pages.retail.name'), { filter: true }),
      Column.text('address', t('pages.retail.address')),
      Column.text('retailerName', t('pages.retail._retailer'), {
        sort: false,
      }),
    ],
    [t],
  );

  const rowSelection = useMemo(
    () => ({
      onChange: (selectedRowKeys) => setValue('posId', ...selectedRowKeys),
      selectedRowKeys: [posId],
      getCheckboxProps: ({ retailer, deleted }) => ({
        disabled: !retailer || retailer !== retailerId || deleted,
      }),
    }),
    [posId, setValue, retailerId],
  );

  const { pagination, content, handleChange } = useDatasource((args) => {
    return getRetailPoses({
      ...args,
      criterias: {
        ...args.criterias,
        retailerId,
        nullable: true,
        deleted: false,
      },
    });
  });

  const BASE_PAGE_SIZE = 10;

  const poses = useMemo(() => {
    const activePoses = content.filter(({ deleted }) => !deleted);

    if (activePoses.length) {
      return [{ id: null, name: 'NONE', retailer: retailerId }, ...activePoses];
    }
    return [];
  }, [content, retailerId]);

  // Always show 11 items per page (10 + NONE)
  const displayPagination = useMemo(
    () => ({
      ...pagination,
      total:
        pagination.total +
        Number(String(pagination.total > 9 ? pagination.total : 0)[0]),
      pageSize: BASE_PAGE_SIZE + 1,
    }),
    [pagination],
  );

  const handleTableChange = useMemo(() => {
    return (paginationConfig, filters, sorter) => {
      const normalizedPagination = {
        ...paginationConfig,
        pageSize: BASE_PAGE_SIZE, // Always fetch with base page size
      };
      handleChange(normalizedPagination, filters, sorter);
    };
  }, [handleChange]);

  return (
    <>
      <FormWrapper.ErrorMessage message={errorMessages?.retailer} />
      <Table
        rowSelection={{
          type: 'radio',
          ...rowSelection,
          getCheckboxProps: () => ({
            disabled: !canEdit,
          }),
        }}
        columns={columns}
        rowKey="id"
        pagination={displayPagination}
        dataSource={poses}
        onChange={handleTableChange}
      />
    </>
  );
};

export default RetailPosList;
